/**
 * Centralised route configuration.
 *
 * Post-dash-v2-rebuild: only public, auth, marketing, trust-link, legal,
 * and mobile pages are exported here. The old dashboard pages are quarantined
 * under src/pages/_legacy/ and excluded from the build via tsconfig.app.json.
 * The new v2 dashboard pages will be added to this barrel as they land.
 */

import { lazyRetry } from "@/lib/lazy-retry";

// Eager: only truly critical-path pages that must render instantly
export { default as NotFound } from "@/pages/NotFound";
export { default as AuthCallback } from "@/pages/AuthCallback";
export { default as Index } from "@/pages/Index";
export const Login = lazyRetry(() => import("@/pages/Login"), "Login");
export const Signup = lazyRetry(() => import("@/pages/Signup"), "Signup");
// Post-signup role picker: shown once after email verification or Google OAuth
// for brand-new users, before they enter the onboarding wizard.
export const SignupRolePicker = lazyRetry(() => import("@/pages/SignupRolePicker"), "SignupRolePicker");

// Auth
export const MfaVerify = lazyRetry(() => import("@/pages/MfaVerify"));
export const ForgotPassword = lazyRetry(() => import("@/pages/ForgotPassword"));
export const ResetPassword = lazyRetry(() => import("@/pages/ResetPassword"));
export const VerifyEmail = lazyRetry(() => import("@/pages/VerifyEmail"));

// Marketing + public content
export const DocsPage = lazyRetry(() => import("@/pages/DocsPage"));
export const AboutPage = lazyRetry(() => import("@/pages/AboutPage"));
export const PricingPage = lazyRetry(() => import("@/pages/PricingPage"));
export const SwitchingFromCarrier411Page = lazyRetry(() => import("@/pages/SwitchingFromCarrier411Page"));
export const CoveragePage = lazyRetry(() => import("@/pages/CoveragePage"));
export const AgentSignupPage = lazyRetry(() => import("@/pages/AgentSignupPage"));
export const ConnectPage = lazyRetry(() => import("@/pages/ConnectPage"));
export const ClaimAgentPage = lazyRetry(() => import("@/pages/ClaimAgentPage"));
export const CarrierOnboarding = lazyRetry(() => import("@/pages/CarrierOnboarding"));
export const ScaleDemoPage = lazyRetry(() => import("@/pages/ScaleDemoPage"));
export const DemoVideoPage = lazyRetry(() => import("@/pages/DemoVideoPage"));
export const DashboardDemoPage = lazyRetry(() => import("@/pages/DashboardDemoPage"));
export const IntegrationGuidePage = lazyRetry(() => import("@/pages/IntegrationGuidePage"));
export const BlogIndex = lazyRetry(() => import("@/pages/BlogIndex"));
export const BlogArticle = lazyRetry(() => import("@/pages/BlogArticle"));
export const BookDemoPage = lazyRetry(() => import("@/pages/BookDemoPage"));
export const PublicCarrierLookup = lazyRetry(() => import("@/pages/PublicCarrierLookup"));
// Programmatic SEO landing pages: /carriers/:region and /brokers/:region.
// One template, ~30 jurisdictions × 2 roles. See src/lib/jurisdictions.ts.
export const StateRegistryPage = lazyRetry(() => import("@/pages/StateRegistryPage"));
// CR Registry · public registry home (Wave 2.7).
// Mounted at /registry. Feature-flagged in App.tsx via `public_registry`.
export const RegistryHomePage = lazyRetry(() => import("@/pages/public/RegistryHomePage"));
// CR Registry · public registry detail (Wave 2.8) — /registry/:cr_id.
// Feature-flagged via `public_registry` (parent flag).
export const RegistryDetailPage = lazyRetry(() => import("@/pages/public/RegistryDetailPage"));
// CR Registry · SEO-canonical profile (Wave 2.8) —
// /registry/cr/:cr_id/:slug. Feature-flagged via `registry_seo_pages`.
// Same data as the detail page but rendered with semantic HTML and
// schema.org Organization markup so search engines index it.
export const RegistrySeoPage = lazyRetry(() => import("@/pages/public/RegistrySeoPage"));
export const QuickstartPage = lazyRetry(() => import("@/pages/QuickstartPage"));
export const ProductPage = lazyRetry(() => import("@/pages/ProductPage"));
export const UnsubscribePage = lazyRetry(() => import("@/pages/UnsubscribePage"));
// Homepage v2 preview · /preview-home. Temporary public route until the
// new homepage replaces /. Delete this export and its route in App.tsx
// once the swap is approved.
export const HomeV2 = lazyRetry(() => import("@/pages/HomeV2"));

// Public trust link + carrier profile (no login required)
export const TrustLinkDriverPage = lazyRetry(() => import("@/pages/TrustLinkDriverPage"));
export const CarrierProfilePage = lazyRetry(() => import("@/pages/CarrierProfilePage"));
export const BrokerProfilePage = lazyRetry(() => import("@/pages/BrokerProfilePage"));
export const ClaimVerifyPage = lazyRetry(() => import("@/pages/ClaimVerifyPage"));
export const TrustLinkReceiptPage = lazyRetry(() => import("@/pages/TrustLinkReceiptPage"));

// Legal
export const SecurityPage = lazyRetry(() => import("@/pages/SecurityPage"));
export const TermsPage = lazyRetry(() => import("@/pages/TermsPage"));
export const PrivacyPage = lazyRetry(() => import("@/pages/PrivacyPage"));
export const ApiTermsPage = lazyRetry(() => import("@/pages/ApiTermsPage"));
export const CookiePolicyPage = lazyRetry(() => import("@/pages/CookiePolicyPage"));

// Trust pages (status, changelog) · canonical truth instead of external subdomain
export const StatusPage = lazyRetry(() => import("@/pages/StatusPage"));
export const ChangelogPage = lazyRetry(() => import("@/pages/ChangelogPage"));

// Mobile views (public, token-scoped)
export const MobileLayout = lazyRetry(() => import("@/pages/mobile/MobileLayout"));
export const MobileLoadFeed = lazyRetry(() => import("@/pages/mobile/MobileLoadFeed"));
export const MobileProfile = lazyRetry(() => import("@/pages/mobile/MobileProfile"));
export const MobileAlerts = lazyRetry(() => import("@/pages/mobile/MobileAlerts"));
